/**
 * App Environment Configuration
 *
 * Uses config.json as the single source of truth for network configurations
 * Supports runtime network switching via Pinia store
 */

// Import config.json
import configData from '@/assets/config.json';

// ========================================
// CONSTANTS
// ========================================
export const NETWORKS = {
	SOLANA: 'SOLANA',
	FOGO: 'FOGO',
} as const;

export const ENVIRONMENTS = {
	MAINNET: 'MAINNET',
	TESTNET: 'TESTNET',
} as const;

export const NETWORK_IDS = {
	SOL_MAINNET: 'SOL_MAINNET',
	FOGO_MAINNET: 'FOGO_MAINNET',
	FOGO_TESTNET: 'FOGO_TESTNET',
} as const;

export type Network = (typeof NETWORKS)[keyof typeof NETWORKS];
export type Environment = (typeof ENVIRONMENTS)[keyof typeof ENVIRONMENTS];
export type NetworkId = (typeof NETWORK_IDS)[keyof typeof NETWORK_IDS];

// ========================================
// TYPE DEFINITIONS
// ========================================
export interface NetworkConfig {
	id: string;
	network: string;
	environment: string;
	displayName: string;
	symbol: string;
	enabled: boolean;
	availableInBeta: boolean;
	availableInProd: boolean;
	urls: {
		data: string;
		api: string;
		cdn: string;
		rpc: string;
		ws: string;
		smartmoney: string;
		swap: string;
		gateway: string;
		bot: string;
		fluxbot: string;
		backupCDN: string;
		legacy: string;
		ipfs: string;
		scratchcard: string;
		leaderboard: string;
		rugcheck: string;
		rugcheckShield?: string;
	};
}

export interface AppEnvironment {
	networkId: string;
	network: Network;
	environment: Environment;
	displayName: string;
	symbol: string;
	isMainnet: boolean;
	isTestnet: boolean;
	prefix: string;
	networkPrefix: string;
	deploymentEnv: string;
	urls: {
		data: string; // Data API
		api: string; // Main API
		cdn: string; // CDN for token images
		rpc: string; // RPC endpoint
		ws: string; // WebSocket endpoint
		smartmoney: string; // Smart Money API
		swap: string; // Swap API
		gateway: string; // Gateway API
		bot: string; // Bot API
		fluxbot: string; // FluxBot Telegram
		backupCDN: string; // Backup CDN
		legacy: string; // Legacy API
		ipfs: string; // IPFS gateway
		scratchcard: string; // Scratchcard API
		leaderboard: string; // Leaderboard API
		rugcheck: string; // Rugcheck API
		rugcheckShield?: string; // Rugcheck shield API, only on networks with token search
	};
}

// ========================================
// CORE LOGIC
// ========================================

/**
 * Determine if we're in production or beta based on URL
 */
function getDeploymentEnvironment(): string {
	if (typeof window === 'undefined') {
		// Server-side or build time
		return process.env.VUE_APP_DEPLOYMENT_ENV || 'beta';
	}

	const hostname = window.location.hostname;

	// Check for beta subdomain or localhost
	if (
		hostname.includes('beta.') ||
		hostname.includes('localhost') ||
		hostname.includes('local') ||
		hostname.includes('127.0.0.1')
	) {
		return 'beta';
	}

	// Production
	return 'prod';
}

/**
 * Get available networks based on deployment environment
 */
export function getAvailableNetworks(): NetworkConfig[] {
	const deploymentEnv = getDeploymentEnvironment();
	const isProd = deploymentEnv === 'prod';

	return Object.values(configData.networks).filter((network: any) => {
		if (isProd) {
			return network.enabled && network.availableInProd;
		} else {
			return network.enabled && network.availableInBeta;
		}
	}) as NetworkConfig[];
}

/**
 * Get default network for current deployment environment
 */
function getDefaultNetworkId(): string {
	const deploymentEnv = getDeploymentEnvironment();
	return (configData.defaultNetwork as any)[deploymentEnv] || 'SOL_MAINNET';
}

/**
 * Load saved network from localStorage or use default
 */
function getInitialNetworkId(): string {
	if (typeof window === 'undefined') {
		return getDefaultNetworkId();
	}

	const saved = localStorage.getItem('selectedNetwork');
	if (saved) {
		// Verify it's available in current deployment environment
		const available = getAvailableNetworks();
		const savedNetwork = available.find((n) => n.id === saved);

		if (savedNetwork) {
			return saved;
		}
	}

	return getDefaultNetworkId();
}

/**
 * Create app environment configuration from network ID
 */
function createAppEnvironmentFromNetworkId(networkId: string): AppEnvironment {
	const networkConfig = (configData.networks as any)[networkId] as NetworkConfig;

	if (!networkConfig) {
		console.error(`Network configuration not found for: ${networkId}`);
		// Fallback to default
		return createAppEnvironmentFromNetworkId(getDefaultNetworkId());
	}

	const isMainnet = networkConfig.environment === 'MAINNET';
	const isTestnet = networkConfig.environment === 'TESTNET';

	// Determine prefix for backwards compatibility
	const networkPrefix = networkConfig.network === 'FOGO' ? 'FOGO' : 'SOL';
	const prefix = isTestnet ? `${networkPrefix}_TESTNET` : networkPrefix;

	return {
		networkId: networkConfig.id,
		network: networkConfig.network as Network,
		environment: networkConfig.environment as Environment,
		displayName: networkConfig.displayName,
		symbol: networkConfig.symbol,
		isMainnet,
		isTestnet,
		prefix,
		networkPrefix,
		deploymentEnv: getDeploymentEnvironment(),
		urls: {
			data: networkConfig.urls.data,
			api: networkConfig.urls.api,
			cdn: networkConfig.urls.cdn,
			rpc: networkConfig.urls.rpc,
			ws: networkConfig.urls.ws,
			smartmoney: networkConfig.urls.smartmoney,
			swap: networkConfig.urls.swap,
			gateway: networkConfig.urls.gateway,
			bot: networkConfig.urls.bot,
			fluxbot: networkConfig.urls.fluxbot,
			backupCDN: networkConfig.urls.backupCDN,
			legacy: networkConfig.urls.legacy,
			ipfs: networkConfig.urls.ipfs,
			scratchcard: networkConfig.urls.scratchcard,
			leaderboard: networkConfig.urls.leaderboard,
			rugcheck: networkConfig.urls.rugcheck,
			rugcheckShield: networkConfig.urls.rugcheckShield,
		},
	};
}

// ========================================
// MUTABLE APP ENVIRONMENT
// ========================================

// Create mutable environment that can be switched at runtime
// Using a plain object instead of reactive() to avoid Vue dependency issues
// The Pinia store will handle reactivity
let currentEnvironment: AppEnvironment = createAppEnvironmentFromNetworkId(getInitialNetworkId());

// Export as a getter/setter proxy to maintain reactivity in components
export const appEnvironment = new Proxy({} as AppEnvironment, {
	get(target, prop) {
		return currentEnvironment[prop as keyof AppEnvironment];
	},
	set(target, prop, value) {
		(currentEnvironment as any)[prop] = value;
		return true;
	},
});

/**
 * Switch to a different network
 */
export function switchNetwork(networkId: string): void {
	const newEnv = createAppEnvironmentFromNetworkId(networkId);

	// Update current environment
	currentEnvironment = newEnv;

	// Update all properties on the proxy
	Object.keys(newEnv).forEach((key) => {
		(appEnvironment as any)[key] = (newEnv as any)[key];
	});

	// Save to localStorage
	if (typeof window !== 'undefined') {
		localStorage.setItem('selectedNetwork', networkId);
	}

	// Log configuration (only in development)
	if (process.env.NODE_ENV === 'development') {
		console.log('🌐 Switched to Network:', {
			networkId: appEnvironment.networkId,
			network: appEnvironment.network,
			environment: appEnvironment.environment,
			displayName: appEnvironment.displayName,
		});
	}
}

// ========================================
// EXPORTS
// ========================================

// Convenience helpers (keep these for backward compatibility)
export const getNetwork = (): string => appEnvironment.network;
export const getEnvironment = (): string => appEnvironment.environment;
export const getDisplayName = (): string => appEnvironment.displayName;

// Network checkers
export const isFogo = (): boolean => appEnvironment.network === NETWORKS.FOGO;
export const isSolana = (): boolean => appEnvironment.network === NETWORKS.SOLANA;

// Environment checkers
export const isMainnet = (): boolean => appEnvironment.isMainnet;
export const isTestnet = (): boolean => appEnvironment.isTestnet;

// Combined checkers
export const isFogoMainnet = (): boolean => isFogo() && isMainnet();
export const isFogoTestnet = (): boolean => isFogo() && isTestnet();
export const isSolanaMainnet = (): boolean => isSolana() && isMainnet();
export const isSolanaTestnet = (): boolean => isSolana() && isTestnet();
export const isDev = (): boolean => process.env.NODE_ENV === 'development';

// Default export
export default appEnvironment;
